import { __ } from '@wordpress/i18n';
import { useBlockProps, InspectorControls, PanelColorSettings } from '@wordpress/block-editor';
import { PanelBody, SelectControl, TextControl, ToggleControl, FormTokenField, RangeControl, __experimentalUnitControl as UnitControl, __experimentalBorderBoxControl as BorderBoxControl, __experimentalBoxControl as BoxControl } from '@wordpress/components';
import { useSelect } from '@wordpress/data';
import ServerSideRender from '@wordpress/server-side-render';
import './editor.scss';

export default function Edit({ attributes, setAttributes }) {
    const blockProps = useBlockProps();

    /* Check if Forumax Pro is active. */
    const isProActive =
        window.bbpc_upsell_config?.is_pro_active === '1' ||
        window.bbpc_upsell_config?.is_pro_active === true;
    const { ppp, order, orderBy, layout, columns, pagination_type, selectedForums, show_meta, showAvatar, showExcerpt, showForumLink, title_color, meta_color, bg_color, title_font_size, title_font_weight, title_line_height, meta_font_size, meta_line_height, card_border, card_shadow, card_padding, card_gap, card_hover_bg, card_hover_border, pagination_text_color, pagination_active_bg, pagination_active_text } = attributes;

    const forums = useSelect((select) => {
        return select('core').getEntityRecords('postType', 'forum', { per_page: -1 });
    }, []);

    const forumOptions = forums ? forums.map(forum => ({ value: forum.id, label: forum.title.rendered })) : [];
    
    const forumSuggestions = forumOptions.map(f => f.label);
    const selectedForumNames = selectedForums.map(id => {
        const f = forumOptions.find(opt => opt.value === id);
        return f ? f.label : id;
    });

    return (
        <div {...blockProps}>
            <InspectorControls>
                <PanelBody title={__('Filter Options', 'bbp-core')}>
                    <TextControl
                        label={__('Show Forum Topics', 'bbp-core')}
                        type="number"
                        value={ppp}
                        onChange={(value) => setAttributes({ ppp: parseInt(value) })}
                    />
                    
                    {/* Forum Selection */}
                    { forums && (
                        <div className={ ! isProActive ? 'bbpc-pro-gated' : '' }>
                            { ! isProActive && (
                                <span className="bbpc-pro-watermark">PRO</span>
                            ) }
                            <FormTokenField
                                label={__('Filter by Forums', 'bbp-core')}
                                value={ isProActive ? selectedForumNames : [] }
                                suggestions={forumSuggestions}
                                onChange={(tokens) => {
                                    if ( isProActive ) {
                                        const newForums = tokens.map(token => {
                                            const forum = forumOptions.find(f => f.label === token);
                                            return forum ? forum.value : null;
                                        }).filter(Boolean);
                                        setAttributes({ selectedForums: newForums });
                                    }
                                }}
                                help={__('Start typing to select forums.', 'bbp-core')}
                                disabled={ ! isProActive }
                            />
                        </div>
                    )}

                    <SelectControl
                        label={__('Order', 'bbp-core')}
                        value={order}
                        options={[
                            { label: 'ASC', value: 'ASC' },
                            { label: 'DESC', value: 'DESC' },
                        ]}
                        onChange={(value) => setAttributes({ order: value })}
                    />
                     <SelectControl
                        label={__('Order By', 'bbp-core')}
                        value={orderBy}
                        options={[
                            { label: __('Date', 'bbp-core'), value: 'date' },
                            { label: __('Last Active', 'bbp-core'), value: 'meta_value' }, // specialized handling in PHP
                            { label: __('Most Replied', 'bbp-core'), value: 'comment_count' },
                            { label: __('Alphabetical', 'bbp-core'), value: 'title' },
                        ]}
                        onChange={(value) => setAttributes({ orderBy: value })}
                    />
                </PanelBody>
                
                <PanelBody title={__('Layout', 'bbp-core')}>
                    <div className={ ! isProActive ? 'bbpc-pro-gated' : '' }>
                        { ! isProActive && (
                            <span className="bbpc-pro-watermark">PRO</span>
                        ) }
                        <SelectControl
                            label={__('Layout Style', 'bbp-core')}
                            value={ isProActive ? layout : 'list' }
                            options={[
                                { label: __('List View', 'bbp-core'), value: 'list' },
                                { label: __('Grid View', 'bbp-core'), value: 'grid' },
                            ]}
                            onChange={(value) => {
                                if ( isProActive ) {
                                    setAttributes({ layout: value });
                                }
                            }}
                            disabled={ ! isProActive }
                        />
                        { layout === 'grid' && isProActive && (
                            <RangeControl
                                label={__('Columns', 'bbp-core')}
                                value={columns}
                                onChange={(value) => setAttributes({ columns: value })}
                                min={2}
                                max={4}
                            />
                        )}
                    </div>
                    <div className={ ! isProActive ? 'bbpc-pro-gated' : '' }>
                        { ! isProActive && (
                            <span className="bbpc-pro-watermark">PRO</span>
                        ) }
                        <SelectControl
                            label={__('Pagination Type', 'bbp-core')}
                            value={ isProActive ? pagination_type : 'none' }
                            options={[
                                { label: __('None', 'bbp-core'), value: 'none' },
                                { label: __('Numeric Pagination', 'bbp-core'), value: 'numbers' },
                                { label: __('Load More Button', 'bbp-core'), value: 'load_more' },
                            ]}
                            onChange={(value) => {
                                if ( isProActive ) {
                                    setAttributes({ pagination_type: value });
                                }
                            }}
                            disabled={ ! isProActive }
                        />
                    </div>
                </PanelBody>
                
                <PanelBody title={__('Display Settings', 'bbp-core')}>
                    <ToggleControl
                        label={__('Show Avatar', 'bbp-core')}
                        checked={showAvatar}
                        onChange={(value) => setAttributes({ showAvatar: value })}
                    />
                    <div className={ ! isProActive ? 'bbpc-pro-gated' : '' }>
                        { ! isProActive && (
                            <span className="bbpc-pro-watermark">PRO</span>
                        ) }
                        <ToggleControl
                            label={__('Show Excerpt', 'bbp-core')}
                            checked={ isProActive ? showExcerpt : false }
                            onChange={(value) => {
                                if ( isProActive ) {
                                    setAttributes({ showExcerpt: value });
                                }
                            }}
                            disabled={ ! isProActive }
                        />
                        <ToggleControl
                            label={__('Show Forum Link', 'bbp-core')}
                            checked={ isProActive ? showForumLink : false }
                            onChange={(value) => {
                                if ( isProActive ) {
                                    setAttributes({ showForumLink: value });
                                }
                            }}
                            disabled={ ! isProActive }
                        />
                    </div>
                </PanelBody>

                <PanelBody title={__('Advanced', 'bbp-core')} initialOpen={false}>
                    <ToggleControl
                        label={__('Show Meta (Replies/Favs)', 'bbp-core')}
                        checked={show_meta}
                        onChange={(value) => setAttributes({ show_meta: value })}
                    />
                </PanelBody>
            </InspectorControls>

            <InspectorControls group="styles">
                <PanelBody title={__('Colors', 'bbp-core')} initialOpen={false}>
                    <PanelColorSettings
                        colorSettings={[
                            {
                                value: title_color,
                                onChange: (value) => setAttributes({ title_color: value }),
                                label: __('Title Color', 'bbp-core'),
                            },
                            {
                                value: meta_color,
                                onChange: (value) => setAttributes({ meta_color: value }),
                                label: __('Meta Color', 'bbp-core'),
                            },
                            {
                                value: bg_color,
                                onChange: (value) => setAttributes({ bg_color: value }),
                                label: __('Background Color', 'bbp-core'),
                            },
                            {
                                value: card_hover_bg,
                                onChange: (value) => setAttributes({ card_hover_bg: value }),
                                label: __('Hover Background', 'bbp-core'),
                            },
                            {
                                value: card_hover_border,
                                onChange: (value) => setAttributes({ card_hover_border: value }),
                                label: __('Hover Border', 'bbp-core'),
                            },
                        ]}
                    />
                    
                    {pagination_type !== 'none' && (
                        <>
                            <hr style={{ margin: '20px 0', border: 'none', borderTop: '1px solid #ddd' }} />
                            <div style={{ marginBottom: '8px', fontSize: '11px', fontWeight: '500', color: '#757575', textTransform: 'uppercase' }}>
                                {__('Pagination Colors', 'bbp-core')}
                            </div>
                            <PanelColorSettings
                                colorSettings={[
                                    {
                                        value: pagination_text_color,
                                        onChange: (value) => setAttributes({ pagination_text_color: value }),
                                        label: __('Text Color', 'bbp-core'),
                                    },
                                    {
                                        value: pagination_active_bg,
                                        onChange: (value) => setAttributes({ pagination_active_bg: value }),
                                        label: __('Active Background', 'bbp-core'),
                                    },
                                    {
                                        value: pagination_active_text,
                                        onChange: (value) => setAttributes({ pagination_active_text: value }),
                                        label: __('Active Text', 'bbp-core'),
                                    },
                                ]}
                            />
                        </>
                    )}
                </PanelBody>

                <PanelBody title={__('Card Styling', 'bbp-core')}>
                    <div style={{ marginBottom: '20px' }}>
                        <BorderBoxControl
                            label={__('Border', 'bbp-core')}
                            value={card_border}
                            onChange={(value) => setAttributes({ card_border: value })}
                        />
                    </div>
                    
                    <div style={{ marginBottom: '20px' }}>
                        <SelectControl
                            label={__('Shadow', 'bbp-core')}
                            value={card_shadow}
                            options={[
                                { label: __('None', 'bbp-core'), value: '' },
                                { label: __('Small', 'bbp-core'), value: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)' },
                                { label: __('Medium', 'bbp-core'), value: '0 3px 6px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.12)' },
                                { label: __('Large', 'bbp-core'), value: '0 10px 20px rgba(0,0,0,0.15), 0 3px 6px rgba(0,0,0,0.10)' },
                                { label: __('Soft', 'bbp-core'), value: '0 2px 8px rgba(0,0,0,0.08)' },
                            ]}
                            onChange={(value) => setAttributes({ card_shadow: value })}
                        />
                    </div>
                    
                    <div style={{ marginBottom: '20px' }}>
                        <RangeControl
                            label={__('Border Radius', 'bbp-core')}
                            value={card_border?.radius ? parseInt(card_border.radius) : 8}
                            onChange={(value) => {
                                const newBorder = { ...card_border, radius: value + 'px' };
                                setAttributes({ card_border: newBorder });
                            }}
                            min={0}
                            max={50}
                            step={1}
                        />
                    </div>
                    
                    <div style={{ 
                        borderTop: '1px solid #ddd', 
                        paddingTop: '16px',
                        marginTop: '16px',
                        marginBottom: '20px'
                    }}>
                        <BoxControl
                            label={__('Padding', 'bbp-core')}
                            values={card_padding}
                            onChange={(value) => setAttributes({ card_padding: value })}
                        />
                    </div>
                    
                    <div>
                        <RangeControl
                            label={__('Gap Between Cards', 'bbp-core')}
                            value={card_gap}
                            onChange={(value) => setAttributes({ card_gap: value })}
                            min={0}
                            max={60}
                            step={5}
                        />
                    </div>
                </PanelBody>

                <PanelBody title={__('Typography', 'bbp-core')} initialOpen={false}>
                    <div style={{ marginBottom: '20px' }}>
                        <strong style={{ display: 'block', marginBottom: '8px', fontSize: '11px', textTransform: 'uppercase', color: '#757575' }}>
                            {__('Title', 'bbp-core')}
                        </strong>
                        <RangeControl
                            label={__('Size', 'bbp-core')}
                            value={title_font_size}
                            onChange={(value) => setAttributes({ title_font_size: value })}
                            min={12}
                            max={48}
                        />
                        <SelectControl
                            label={__('Weight', 'bbp-core')}
                            value={title_font_weight}
                            options={[
                                { label: '400', value: '400' },
                                { label: '500', value: '500' },
                                { label: '600', value: '600' },
                                { label: '700', value: '700' },
                            ]}
                            onChange={(value) => setAttributes({ title_font_weight: value })}
                        />
                        <RangeControl
                            label={__('Line Height', 'bbp-core')}
                            value={title_line_height}
                            onChange={(value) => setAttributes({ title_line_height: value })}
                            min={1}
                            max={3}
                            step={0.1}
                        />
                    </div>
                    
                    <div>
                        <strong style={{ display: 'block', marginBottom: '8px', fontSize: '11px', textTransform: 'uppercase', color: '#757575' }}>
                            {__('Meta', 'bbp-core')}
                        </strong>
                        <RangeControl
                            label={__('Size', 'bbp-core')}
                            value={meta_font_size}
                            onChange={(value) => setAttributes({ meta_font_size: value })}
                            min={10}
                            max={24}
                        />
                        <RangeControl
                            label={__('Line Height', 'bbp-core')}
                            value={meta_line_height}
                            onChange={(value) => setAttributes({ meta_line_height: value })}
                            min={1}
                            max={3}
                            step={0.1}
                        />
                    </div>
                </PanelBody>
            </InspectorControls>

            <ServerSideRender
                block="bbp-core/forum-posts"
                attributes={attributes}
            />
        </div >
    );
}
